
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type SubmitEvent, useMemo, useState } from 'react'

import type { BranchInfo } from '@/bindings/BranchInfo'
import type { HoursMode } from '@/bindings/HoursMode'
import type { RestaurantContext } from '@/bindings/RestaurantContext'
import type { UpdateBranchInfoRequest } from '@/bindings/UpdateBranchInfoRequest'
import type { Weekday } from '@/bindings/Weekday'
import { Button } from '@/components/button'
import { Checkbox, Field, Input, Select, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, branchInfoPath, branchInfoQuery } from '@/features/back-office/api'
import { type NameDraft, namesFrom, toLocalizedText } from '@/features/back-office/name-draft'
import { claim, request } from '@/lib/api'
import { cx } from '@/lib/cx'
import { LANGUAGE_NAMES } from '@/lib/i18n/languages'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { defaultNation, nationOptions } from '@/lib/nations'
import { type DaysDraft, HOURS_MODES, WEEK, dayGroups, daysDraftFrom, groupAnchor, groupLabel, toOpeningHours } from '@/lib/opening-hours'
import { canManageRestaurant } from '@/lib/roles'


/**
 * Branch info page (PRD, H2): name, address, phone, opening hours and the per-language
 * announcement — what the public branch page and the printed tickets show. Manager and above;
 * admins get the notice and nothing loads.
 */
export function BranchInfoPage() {
  const t = useT()
  const { branchId, context } = useManagedBranch()

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('branchInfo.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (!canManageRestaurant(context.role)) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('branchInfo.managerOnly')}
        </p>
      </Page>
    )
  }

  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <Info context={context} branchId={branchId} />
    </Page>
  )
}

function Info({ context, branchId }: { context: RestaurantContext; branchId: string }) {
  const t = useT()
  const info = useQuery(branchInfoQuery(context.settings.id, branchId))
  if (info.isPending) return <p className="text-muted text-sm">{t('common.loading')}</p>
  if (info.error) {
    return (
      <div className="flex flex-col gap-2">
        <FormError error={info.error} />
        <Button variant="secondary" onClick={() => void info.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  return <InfoForm key={info.data.id} context={context} info={info.data} />
}

interface Draft {
  name: string
  address: string
  nation: string
  /** E.164 as stored, or as typed; the backend normalizes under `nation`. */
  phone: string
  /** Unticked saves `null`; the table below is then only a starting point. */
  showHours: boolean
  hoursMode: HoursMode
  days: DaysDraft
  announcement: NameDraft
}

function draftFrom(info: BranchInfo): Draft {
  return {
    name: info.name,
    address: info.address ?? '',
    nation: defaultNation(),
    phone: info.phone ?? '',
    showHours: info.opening_hours !== null,
    hoursMode: info.opening_hours?.mode ?? 'same',
    days: daysDraftFrom(info.opening_hours),
    announcement: namesFrom(info.announcement),
  }
}

/** Blank text clears a field; a blank number is "no phone", not a refusal. */
function toRequest(draft: Draft): UpdateBranchInfoRequest {
  const phone = draft.phone.trim()
  return {
    name: draft.name.trim(),
    address: draft.address.trim() || null,
    phone: phone ? { nation: draft.nation, number: phone } : null,
    opening_hours: draft.showHours ? toOpeningHours(draft.hoursMode, draft.days) : null,
    announcement: toLocalizedText(draft.announcement),
  }
}

function InfoForm({ context, info }: { context: RestaurantContext; info: BranchInfo }) {
  const t = useT()
  const language = useLanguage()
  const queryClient = useQueryClient()
  const nations = useMemo(() => nationOptions(language), [language])
  const [draft, setDraft] = useState(() => draftFrom(info))
  const [saved, setSaved] = useState(false)
  const restaurantId = context.settings.id

  const save = useMutation({
    mutationFn: () => request<BranchInfo>(branchInfoPath(restaurantId, info.id), { method: 'PUT', body: toRequest(draft) }),
    onSuccess: (next) => {
      queryClient.setQueryData(backOfficeKeys.branchInfo(restaurantId, info.id), next)
      // The shell's branch list and the tickets (G14) read the branch from the context.
      void queryClient.invalidateQueries({ queryKey: backOfficeKeys.context(context.settings.slug) })
      setDraft(draftFrom(next))
      setSaved(true)
    },
  })

  const edit = (changes: Partial<Draft>) => {
    if (save.error) save.reset()
    setSaved(false)
    setDraft((current) => ({ ...current, ...changes }))
  }

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const phoneError = claim(save.error, 'INVALID_PHONE')
  const { main_language: main, secondary_languages: secondary } = context.settings
  const shownLanguages = [main, ...secondary]

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Card className="flex flex-col gap-4">
        <p className="text-muted text-sm">{t('branchInfo.hint')}</p>
        <Field label={t('branchInfo.name')}>
          <Input required value={draft.name} onChange={(e) => edit({ name: e.target.value })} />
        </Field>
        <Field label={t('branchInfo.address')} hint={t('common.optional')}>
          <Input autoComplete="street-address" value={draft.address} onChange={(e) => edit({ address: e.target.value })} />
        </Field>
        <Field label={t('auth.nation')}>
          <Select value={draft.nation} onChange={(e) => edit({ nation: e.target.value })}>
            {nations.map((nation) => (
              <option key={nation.code} value={nation.code}>
                {nation.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('branchInfo.phone')} hint={t('branchInfo.phoneHint')} error={phoneError}>
          <Input type="tel" inputMode="tel" autoComplete="tel" value={draft.phone} onChange={(e) => edit({ phone: e.target.value })} />
        </Field>
      </Card>

      <Card className="flex flex-col gap-4" aria-label={t('branchInfo.openingHoursTitle')}>
        <h2 className="text-lg font-semibold">{t('branchInfo.openingHoursTitle')}</h2>
        <p className="text-muted text-sm">{t('branchInfo.openingHoursHint')}</p>
        <Checkbox label={t('branchInfo.showOpeningHours')} checked={draft.showHours} onChange={(e) => edit({ showHours: e.target.checked })} />
        <HoursEditor
          mode={draft.hoursMode}
          days={draft.days}
          disabled={!draft.showHours}
          onMode={(hoursMode) => edit({ hoursMode })}
          onDays={(days) => edit({ days })}
        />
      </Card>

      <Card className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">{t('branchInfo.announcementTitle')}</h2>
        <p className="text-muted text-sm">{t('branchInfo.announcementHint')}</p>
        {shownLanguages.map((lang) => (
          <Field key={lang} label={t('branchInfo.announcementFor', { language: LANGUAGE_NAMES[lang] })} hint={t('common.optional')}>
            <Textarea
              rows={3}
              value={draft.announcement[lang]}
              onChange={(e) => edit({ announcement: { ...draft.announcement, [lang]: e.target.value } })}
            />
          </Field>
        ))}
      </Card>

      <FormError error={phoneError ? undefined : save.error} />
      <div className="flex items-center gap-3">
        <Button type="submit" loading={save.isPending}>
          {t('common.save')}
        </Button>
        {saved && (
          <p role="status" className="text-muted text-sm">
            {t('branchInfo.saved')}
          </p>
        )}
      </div>
    </form>
  )
}

/**
 * The old project's editor: one open/closed tick per day on the left; on the right one time
 * range per group of days the mode merges (all week, weekdays + weekend, or each day).
 */
function HoursEditor({
  mode,
  days,
  disabled,
  onMode,
  onDays,
}: {
  mode: HoursMode
  days: DaysDraft
  disabled: boolean
  onMode: (mode: HoursMode) => void
  onDays: (days: DaysDraft) => void
}) {
  const t = useT()
  const setClosed = (day: Weekday, closed: boolean) => onDays({ ...days, [day]: { ...days[day], closed } })
  const setTimes = (group: readonly Weekday[], patch: { open?: string; close?: string }) => {
    const next = { ...days }
    for (const day of group) next[day] = { ...next[day], ...patch }
    onDays(next)
  }
  const groups = dayGroups(mode)
  const rowOf = (day: Weekday) => WEEK.indexOf(day) + 1

  return (
    <div className={cx('flex flex-col gap-3', disabled && 'opacity-50')}>
      <div role="group" aria-label={t('branchInfo.openingHoursTitle')} className="border-border grid grid-cols-3 gap-1 rounded-lg border p-1">
        {HOURS_MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            disabled={disabled}
            aria-pressed={mode === candidate}
            onClick={() => onMode(candidate)}
            className={cx('min-h-9 rounded-md text-sm font-medium', mode === candidate ? 'bg-accent text-accent-fg' : 'text-fg hover:bg-accent-soft')}
          >
            {t(`openingHours.mode.${candidate}`)}
          </button>
        ))}
      </div>
      <div className="border-border grid grid-cols-[auto_1fr] overflow-hidden rounded-lg border">
        {WEEK.map((day) => (
          <div key={day} style={{ gridColumn: 1, gridRow: rowOf(day) }} className={cx('border-border px-3', rowOf(day) > 1 && 'border-t')}>
            <Checkbox
              label={<span className={cx('text-sm', days[day].closed && 'text-muted line-through')}>{t(`day.${day}`)}</span>}
              checked={!days[day].closed}
              disabled={disabled}
              onChange={(e) => setClosed(day, !e.target.checked)}
            />
          </div>
        ))}
        {groups.map((group) => {
          const first = group[0]!
          const anchor = groupAnchor(group, days)
          const label = groupLabel(group, t)
          const allClosed = group.every((day) => days[day].closed)
          return (
            <div
              key={first}
              style={{ gridColumn: 2, gridRow: `${rowOf(first)} / span ${group.length}` }}
              className={cx('border-border flex items-center border-l px-3 py-2', rowOf(first) > 1 && 'border-t')}
            >
              {allClosed ? (
                <span className="text-muted text-sm">{t('openingHours.closed')}</span>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="time"
                    aria-label={`${label} ${t('openingHours.opens')}`}
                    disabled={disabled}
                    required
                    value={days[anchor].open}
                    onChange={(e) => setTimes(group, { open: e.target.value })}
                    className="w-32"
                  />
                  <span className="text-muted text-sm">{t('openingHours.to')}</span>
                  <Input
                    type="time"
                    aria-label={`${label} ${t('openingHours.closes')}`}
                    disabled={disabled}
                    required
                    value={days[anchor].close}
                    onChange={(e) => setTimes(group, { close: e.target.value })}
                    className="w-32"
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
